if (!customElements.get('v3-pedal-story')) {
  class V3PedalStory extends HTMLElement {
    connectedCallback() {
      this.scrollVideoCleanup?.();
      this.finishSequenceCleanup?.();
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.setupTabs('[data-seal-tab]', '[data-seal-panel]');
      this.setupTabs('[data-pedal-tab]', '[data-pedal-panel]');
      this.setupChapterTracking();
      this.setupScrollVideo();
      this.setupFinishSequence();
      if (!this.reducedMotion) this.setupParallax();
    }

    disconnectedCallback() {
      this.scrollVideoCleanup?.();
      this.finishSequenceCleanup?.();
    }

    setupFinishSequence() {
      const sequence = this.querySelector('[data-finish-sequence]');
      if (!sequence || this.reducedMotion) return;
      const photos = [...sequence.querySelectorAll('.v3-story__finish-photo')];
      if (photos.length < 2) return;
      sequence.classList.add('is-scroll-sequence');
      let frame = 0;
      const update = () => {
        frame = 0;
        const stage = sequence.querySelector('.v3-story__finish-stage');
        const travel = Math.max(1, sequence.offsetHeight - stage.offsetHeight);
        const progress = Math.max(0, Math.min(1, -sequence.getBoundingClientRect().top / travel));
        // Start fading immediately, then release the last photo into normal scrolling.
        const position = progress * (photos.length - 1);
        photos.forEach((photo, index) => {
          const fade = Math.max(0, Math.min(1, position - index + 1));
          photo.style.opacity = index === 0 ? '1' : String(fade * fade * (3 - 2 * fade));
          photo.setAttribute('aria-hidden', String(index !== Math.max(0, Math.min(photos.length - 1, Math.round(position)))));
        });
      };
      const schedule = () => { if (!frame) frame = requestAnimationFrame(update); };
      window.addEventListener('scroll', schedule, { passive: true });
      window.addEventListener('resize', schedule);
      update();
      this.finishSequenceCleanup = () => {
        window.removeEventListener('scroll', schedule);
        window.removeEventListener('resize', schedule);
        cancelAnimationFrame(frame);
        sequence.classList.remove('is-scroll-sequence');
        photos.forEach(photo => { photo.style.opacity = ''; photo.removeAttribute('aria-hidden'); });
      };
    }

    setupTabs(tabSelector, panelSelector) {
      const tabs = [...this.querySelectorAll(tabSelector)];
      const panels = [...this.querySelectorAll(panelSelector)];
      if (!tabs.length || !panels.length) return;

      const sealView = tabSelector === '[data-seal-tab]' ? this.querySelector('[data-seal-view]') : null;
      // Normalized centers shared by the supplied 16:9 exploded renders.
      // Outside → inside at the crank end, then the opposite bearing end.
      const sealPositions = [
        { x: 65.9, y: 32.7, width: 4.7, height: 8.8 },
        { x: 62.5, y: 36.5, width: 4.2, height: 7.6 },
        { x: 59.8, y: 39.3, width: 3.5, height: 6.8 },
        { x: 21.0, y: 81.0, width: 3.7, height: 8.0 }
      ];
      const focusSeal = (index, overview = false) => {
        if (!sealView) return;
        const point = { ...sealPositions[index] };
        ['x', 'y', 'width', 'height'].forEach(axis => {
          const value = tabs[index].getAttribute('data-focus-' + axis);
          if (value !== null && Number.isFinite(Number(value))) point[axis] = Number(value);
        });
        sealView.style.setProperty('--seal-x', `${overview ? 50 : point.x}%`);
        sealView.style.setProperty('--seal-y', `${overview ? 50 : point.y}%`);
        sealView.style.setProperty('--seal-zoom', overview ? 1 : 3.8);
        const highlight = sealView.querySelector('[data-seal-highlight]');
        highlight.style.left = `${point.x}%`;
        highlight.style.top = `${point.y}%`;
        highlight.style.width = `${point.width}%`;
        highlight.style.height = `${point.height}%`;
        sealView.querySelector('[data-seal-view-label]').textContent = overview ? (sealView.dataset.overviewLabel ?? 'Four seals / One complete system') : tabs[index].textContent.trim();
        const reset = sealView.querySelector('[data-seal-reset]');
        reset.textContent = overview ? (sealView.dataset.zoomLabel ?? 'Zoom to selected seal') : (sealView.dataset.resetLabel ?? 'View whole pedal');
        reset.setAttribute('aria-label', overview ? (sealView.dataset.zoomLabel ?? 'Zoom to selected seal') : (sealView.dataset.resetLabel ?? 'View whole pedal'));
        sealView.dataset.overview = String(overview);
      };
      if (sealView) {
        sealView.querySelector('[data-seal-reset]').addEventListener('click', () => {
          const index = tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
          focusSeal(index, sealView.dataset.overview !== 'true');
        });
        focusSeal(0);
      }

      const activate = (tab, focus = false) => {
        tabs.forEach((item) => {
          const selected = item === tab;
          item.setAttribute('aria-selected', selected ? 'true' : 'false');
          item.tabIndex = selected ? 0 : -1;
        });
        panels.forEach((panel) => { panel.hidden = panel.id !== tab.getAttribute('aria-controls'); });
        if (sealView) focusSeal(tabs.indexOf(tab));
        if (focus) tab.focus();
      };

      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => activate(tab));
        tab.addEventListener('keydown', (event) => {
          if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          let next = index;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = tabs.length - 1;
          if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (index + 1) % tabs.length;
          if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (index - 1 + tabs.length) % tabs.length;
          activate(tabs[next], true);
        });
      });
    }

    setupChapterTracking() {
      const links = [...this.querySelectorAll('[data-chapter-link]')];
      const sections = links.map((link) => this.querySelector(link.getAttribute('href'))).filter(Boolean);
      if (!links.length || !sections.length || !('IntersectionObserver' in window)) return;

      const observer = new IntersectionObserver((entries) => {
        const visible = entries.filter((entry) => entry.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (!visible) return;
        links.forEach((link) => link.setAttribute('aria-current', link.getAttribute('href') === `#${visible.target.id}` ? 'true' : 'false'));
      }, { rootMargin: '-28% 0px -60% 0px', threshold: [0, 0.2, 0.5] });

      sections.forEach((section) => observer.observe(section));
    }

    setupScrollVideo() {
      const sequences = [...this.querySelectorAll('[data-scroll-scrub]')];
      if (!sequences.length) return;

      const states = sequences.map((sequence) => ({
        sequence,
        video: sequence.querySelector('.v3-story__exploded-video'),
        startCopy: sequence.querySelector('[data-scrub-start-copy]'),
        endCopy: sequence.querySelector('[data-scrub-end-copy]'),
        progressBar: sequence.querySelector('[data-scrub-progress]'),
        progress: null
      })).filter((state) => state.video);
      const controller = new AbortController();
      let frame = 0;
      let previousTime = 0;

      const updateSequence = (state, delta) => {
        const { sequence, video, startCopy, endCopy, progressBar } = state;
        if (!Number.isFinite(video.duration) || state.priming || video.readyState < 2 || video.error) return false;

        const endTime = Math.min(Number(sequence.dataset.scrubEnd) || 7.5, video.duration);
        if (this.reducedMotion) {
          if (!video.seeking && Math.abs(video.currentTime - endTime) > 0.04) video.currentTime = endTime;
          return false;
        }

        const rect = sequence.getBoundingClientRect();
        // Match the CSS svh-sized sticky stage. Mobile browser toolbars can
        // change innerHeight without changing the stage's actual height.
        const stageHeight = sequence.querySelector('.v3-story__exploded-sticky').offsetHeight;
        const scrollable = Math.max(1, rect.height - stageHeight);
        const targetProgress = Math.min(1, Math.max(0, -rect.top / scrollable));
        // Time-based easing stays consistent on mouse wheels, trackpads and
        // high-refresh displays. Keep easing after the last scroll event.
        if (state.progress === null) state.progress = targetProgress;
        state.progress += (targetProgress - state.progress) * (1 - Math.exp(-delta / 110));
        if (Math.abs(targetProgress - state.progress) < 0.001) state.progress = targetProgress;
        // Finish the sequence if a fast scroll has already carried it offscreen.
        if (rect.bottom <= 0 || rect.top >= window.innerHeight) state.progress = targetProgress;
        const progress = state.progress;
        // A tiny initial seek displays the decoded first frame without waiting
        // for a scroll gesture. It remains inside the first frame at 24 fps.
        const targetTime = Math.max(0.001, progress * endTime);
        const needsSeek = video.currentTime === 0 || Math.abs(video.currentTime - targetTime) > 0.012;
        // Let the decoder finish one seek before issuing the next. Repeatedly
        // cancelling in-flight seeks was causing visible frame stutter.
        if (!video.seeking && needsSeek) {
          try { video.currentTime = targetTime; } catch (_) { return false; }
        }
        const startVisibility = 1 - Math.min(1, progress / 0.22);
        const endVisibility = Math.min(1, Math.max(0, (progress - 0.72) / 0.2));

        if (startCopy) {
          startCopy.style.opacity = startVisibility;
          startCopy.style.transform = `translate3d(0, ${progress * -28}px, 0)`;
        }
        if (endCopy) {
          endCopy.style.opacity = endVisibility;
          endCopy.style.transform = `translate3d(0, ${(1 - endVisibility) * 28}px, 0)`;
        }
        if (progressBar) progressBar.style.transform = `scaleX(${progress})`;
        return progress !== targetProgress || (needsSeek && !video.seeking);
      };

      const update = (time) => {
        frame = 0;
        const delta = previousTime ? Math.min(time - previousTime, 50) : 16.7;
        previousTime = time;
        let moving = false;
        states.forEach((state) => { moving = updateSequence(state, delta) || moving; });
        if (moving) frame = window.requestAnimationFrame(update);
        else previousTime = 0;
      };
      const requestUpdate = () => {
        if (!frame) frame = window.requestAnimationFrame(update);
      };

      const prime = (state) => {
        const { video, sequence } = state;
        if (state.priming || sequence.dataset.videoReady === 'true') return;
        state.priming = true;
        video.muted = true;
        video.playsInline = true;
        if (video.error || video.networkState === 0) video.load();
        const attempt = video.play();
        Promise.resolve(attempt).then(() => {
          video.pause();
          state.priming = false;
          if (!controller.signal.aborted) requestUpdate();
        }).catch(() => {
          state.priming = false;
          if (!controller.signal.aborted) requestUpdate();
        });
      };
      const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) prime(states.find(state => state.sequence === entry.target));
        });
      }, { rootMargin: '300px' });
      states.forEach((state) => {
        const { video, sequence } = state;
        const retry = sequence.querySelector('[data-scrub-retry]');
        retry?.addEventListener('click', () => prime(state), { signal: controller.signal });
        sequence.addEventListener('touchstart', () => prime(state), { passive: true, signal: controller.signal });
        observer.observe(sequence);
        video.addEventListener('loadedmetadata', requestUpdate, { signal: controller.signal });
        video.addEventListener('loadeddata', requestUpdate, { signal: controller.signal });
        video.addEventListener('canplay', requestUpdate, { signal: controller.signal });
        video.addEventListener('seeked', () => {
          if (video.readyState >= 2 && !video.error) {
            sequence.dataset.videoReady = 'true';
            if (retry) retry.hidden = true;
          }
          requestUpdate();
        }, { signal: controller.signal });
        video.addEventListener('error', () => {
          sequence.dataset.videoReady = 'false';
          if (retry) { retry.hidden = false; retry.textContent = 'Retry animation'; }
        }, { signal: controller.signal });
      });
      requestUpdate();
      window.addEventListener('scroll', requestUpdate, { passive: true, signal: controller.signal });
      window.addEventListener('resize', requestUpdate, { signal: controller.signal });
      this.scrollVideoCleanup = () => {
        controller.abort();
        observer.disconnect();
        states.forEach(({ video }) => video.pause());
        window.cancelAnimationFrame(frame);
      };
    }

    setupParallax() {
      const media = [...this.querySelectorAll('[data-parallax]')];
      if (!media.length) return;
      let ticking = false;

      const update = () => {
        media.forEach((item) => {
          const host = item.parentElement;
          const rect = host.getBoundingClientRect();
          if (rect.bottom < 0 || rect.top > window.innerHeight) return;
          const progress = (window.innerHeight - rect.top) / (window.innerHeight + rect.height);
          const travel = (progress - 0.5) * 90;
          item.style.transform = `translate3d(0, ${travel}px, 0)`;
        });
        ticking = false;
      };

      const requestUpdate = () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(update);
      };

      update();
      window.addEventListener('scroll', requestUpdate, { passive: true });
      window.addEventListener('resize', requestUpdate);
    }
  }

  customElements.define('v3-pedal-story', V3PedalStory);
}
